import { useMemo, useCallback, useState } from 'react';
import { Contract, type ContractTransactionResponse, type TransactionReceipt, keccak256, toUtf8Bytes, solidityPackedKeccak256 } from 'ethers';
import { useWallet } from './useWallet';
import { useToast } from '@/contexts/ToastContext';
import { CONTRACT_ADDRESSES, GAS_LIMITS, ZERO_ADDRESS } from '@/lib/contracts/config';
import {
  GameABI,
  CombatABI,
  PartyABI,
  MagicABI,
  WorldABI,
  ShopABI,
  InnABI,
} from '@/lib/contracts/abi';
import type { GameState, CombatState, Character } from '@/lib/types/game';

/**
 * Transaction result type
 */
export interface TransactionResult {
  success: boolean;
  hash?: string;
  error?: string;
  receipt?: TransactionReceipt;
}

/**
 * Direction enum for movement
 */
export enum Direction {
  NORTH = 0,
  EAST = 1,
  SOUTH = 2,
  WEST = 3,
}

/**
 * Comprehensive hook for all smart contract interactions
 * Handles both read and write operations with proper error handling, gas estimation, and event listening
 */
export const useContract = () => {
  const { provider, signer, address, isConnected } = useWallet();
  const toast = useToast();

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Contract instances
  const gameContract = useMemo(() => {
    if (!provider || CONTRACT_ADDRESSES.GAME === ZERO_ADDRESS) {
      return null;
    }
    return new Contract(CONTRACT_ADDRESSES.GAME, GameABI, signer || provider);
  }, [provider, signer]);

  const combatContract = useMemo(() => {
    if (!provider || CONTRACT_ADDRESSES.COMBAT === ZERO_ADDRESS) {
      return null;
    }
    return new Contract(CONTRACT_ADDRESSES.COMBAT, CombatABI, signer || provider);
  }, [provider, signer]);

  const partyContract = useMemo(() => {
    if (!provider || CONTRACT_ADDRESSES.PARTY === ZERO_ADDRESS) {
      return null;
    }
    return new Contract(CONTRACT_ADDRESSES.PARTY, PartyABI, signer || provider);
  }, [provider, signer]);

  const magicContract = useMemo(() => {
    if (!provider || CONTRACT_ADDRESSES.MAGIC === ZERO_ADDRESS) {
      return null;
    }
    return new Contract(CONTRACT_ADDRESSES.MAGIC, MagicABI, signer || provider);
  }, [provider, signer]);

  const worldContract = useMemo(() => {
    if (!provider || CONTRACT_ADDRESSES.WORLD === ZERO_ADDRESS) {
      return null;
    }
    return new Contract(CONTRACT_ADDRESSES.WORLD, WorldABI, signer || provider);
  }, [provider, signer]);

  const shopContract = useMemo(() => {
    if (!provider || CONTRACT_ADDRESSES.SHOP === ZERO_ADDRESS) {
      return null;
    }
    return new Contract(CONTRACT_ADDRESSES.SHOP, ShopABI, signer || provider);
  }, [provider, signer]);

  const innContract = useMemo(() => {
    if (!provider || CONTRACT_ADDRESSES.INN === ZERO_ADDRESS) {
      return null;
    }
    return new Contract(CONTRACT_ADDRESSES.INN, InnABI, signer || provider);
  }, [provider, signer]);

  // Helper function to handle transactions with comprehensive error handling
  const executeTransaction = useCallback(
    async (
      txPromise: Promise<ContractTransactionResponse>,
      successMessage: string,
      errorMessage: string
    ): Promise<TransactionResult> => {
      setIsLoading(true);
      setError(null);

      try {
        const tx = await txPromise;
        toast.info(`Transaction submitted: ${tx.hash.slice(0, 10)}...`);

        const receipt = await tx.wait();

        if (receipt?.status === 1) {
          toast.success(successMessage);
          setIsLoading(false);
          return {
            success: true,
            hash: tx.hash,
            receipt,
          };
        } else {
          const errorMsg = 'Transaction failed';
          toast.error(errorMsg);
          setError(errorMsg);
          setIsLoading(false);
          return {
            success: false,
            error: errorMsg,
            hash: tx.hash,
          };
        }
      } catch (err: unknown) {
        console.error('Transaction error:', err);
        const errorMsg = err instanceof Error ? err.message : errorMessage;
        toast.error(errorMsg);
        setError(errorMsg);
        setIsLoading(false);
        return {
          success: false,
          error: errorMsg,
        };
      }
    },
    [toast]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // GAME MANAGEMENT FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Check if player has an active game
   */
  const hasGame = useCallback(async (): Promise<boolean> => {
    if (!gameContract || !address) return false;
    try {
      return await gameContract.hasGame!(address);
    } catch (error) {
      console.error('Failed to check for active game:', error);
      return false;
    }
  }, [gameContract, address]);

  /**
   * Helper to parse a Character from contract data (converts BigInt to number)
   */
  const parseCharacter = (char: unknown): Character => {
    const c = char as Record<string, bigint | number>;
    return {
      class: Number(c.class_ ?? c.class ?? 0),
      level: Number(c.level ?? 1),
      experience: Number(c.experience ?? 0),
      currentHP: Number(c.currentHP ?? 0),
      maxHP: Number(c.maxHP ?? 1), // Avoid division by zero
      currentMP: Number(c.currentMP ?? 0),
      maxMP: Number(c.maxMP ?? 0),
      strength: Number(c.strength ?? 0),
      agility: Number(c.agility ?? 0),
      intelligence: Number(c.intelligence ?? 0),
      vitality: Number(c.vitality ?? 0),
      luck: Number(c.luck ?? 0),
      weapon: Number(c.weapon ?? 0),
      armor: Number(c.armor ?? 0),
      helmet: Number(c.helmet ?? 0),
      gloves: Number(c.gloves ?? 0),
      accessory: Number(c.accessory ?? 0),
      status: Number(c.status ?? 0),
    };
  };

  /**
   * Get complete game state for the connected player
   */
  const getGameState = useCallback(async (): Promise<GameState | null> => {
    if (!gameContract || !address) return null;
    try {
      // Fetch game state and party in parallel
      const [stateRaw, partyRaw] = await Promise.all([
        gameContract.getGameState!(address),
        gameContract.getParty!(address),
      ]);

      // Parse party members (convert BigInt to number)
      const party = Array.from(partyRaw).map(parseCharacter) as [Character, Character, Character, Character];

      // Parse game state
      const state: GameState = {
        party,
        worldX: Number(stateRaw.worldX ?? 0),
        worldY: Number(stateRaw.worldY ?? 0),
        currentMap: Number(stateRaw.currentMap ?? 0),
        dungeonFloor: Number(stateRaw.dungeonFloor ?? 0),
        facing: Number(stateRaw.facing ?? 0),
        gil: Number(stateRaw.gil ?? 0),
        stepsToEncounter: Number(stateRaw.stepsToEncounter ?? 0),
        storyFlags: BigInt(stateRaw.storyFlags ?? 0),
        inventory: Array.from(stateRaw.inventory || []).map(Number),
        inventoryQty: Array.from(stateRaw.inventoryQty || []).map(Number),
        keyItems: Number(stateRaw.keyItems ?? 0),
        playTime: Number(stateRaw.playTime ?? 0),
        startBlock: Number(stateRaw.startBlock ?? 0),
      };

      console.log('[useContract] getGameState() raw from contract:', {
        worldX: Number(stateRaw.worldX),
        worldY: Number(stateRaw.worldY),
        gil: Number(stateRaw.gil),
        partyHP: party.map(p => p.currentHP),
      });

      return state;
    } catch (error) {
      console.error('Failed to get game state:', error);
      return null;
    }
  }, [gameContract, address]);

  /**
   * Start a new game with selected character classes in a specific slot
   * @param slot Save slot (0-7)
   * @param classes Array of 4 character classes (0-5)
   */
  const startNewGame = useCallback(
    async (slot: number, classes: [number, number, number, number]): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      // Validate slot
      if (slot < 0 || slot > 7) {
        const error = 'Invalid save slot selected';
        toast.error(error);
        return { success: false, error };
      }

      // Validate classes
      if (classes.some(c => c < 0 || c > 5)) {
        const error = 'Invalid character class selected';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        gameContract.startNewGame!(slot, classes, { gasLimit: GAS_LIMITS.CREATE_GAME }),
        'Game created successfully! Your adventure begins!',
        'Failed to create game'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Select which save slot to play
   * @param slot Save slot (0-7)
   */
  const selectSlot = useCallback(
    async (slot: number): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      if (slot < 0 || slot > 7) {
        const error = 'Invalid save slot';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        gameContract.selectSlot!(slot, { gasLimit: 50000 }),
        'Save slot selected!',
        'Failed to select slot'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Delete a save slot
   * @param slot Save slot to delete (0-7)
   */
  const deleteSlot = useCallback(
    async (slot: number): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      if (slot < 0 || slot > 7) {
        const error = 'Invalid save slot';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        gameContract.deleteSlot!(slot, { gasLimit: 100000 }),
        'Save slot deleted!',
        'Failed to delete slot'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Get info about all save slots for the connected player
   * @returns Object with slots array (8 booleans) and current active slot
   */
  const getSlotInfo = useCallback(async (): Promise<{ slots: boolean[]; current: number } | null> => {
    if (!gameContract || !address) return null;
    try {
      const [slots, current] = await gameContract.getSlotInfo!(address);
      return {
        slots: Array.from(slots) as boolean[],
        current: Number(current),
      };
    } catch (error) {
      console.error('Failed to get slot info:', error);
      return null;
    }
  }, [gameContract, address]);

  /**
   * Get game state for a specific slot
   * @param slot Save slot (0-7)
   */
  const getGameStateForSlot = useCallback(async (slot: number): Promise<GameState | null> => {
    if (!gameContract || !address) return null;
    try {
      // Fetch game state and party for the slot
      const [stateRaw, partyRaw] = await Promise.all([
        gameContract.getGameStateForSlot!(address, slot),
        gameContract.getPartyForSlot!(address, slot),
      ]);

      // Parse party members (convert BigInt to number)
      const party = Array.from(partyRaw).map(parseCharacter) as [Character, Character, Character, Character];

      // Parse game state
      const state: GameState = {
        party,
        worldX: Number(stateRaw.worldX ?? 0),
        worldY: Number(stateRaw.worldY ?? 0),
        currentMap: Number(stateRaw.currentMap ?? 0),
        dungeonFloor: Number(stateRaw.dungeonFloor ?? 0),
        facing: Number(stateRaw.facing ?? 0),
        gil: Number(stateRaw.gil ?? 0),
        stepsToEncounter: Number(stateRaw.stepsToEncounter ?? 0),
        storyFlags: BigInt(stateRaw.storyFlags ?? 0),
        inventory: Array.from(stateRaw.inventory || []).map(Number),
        inventoryQty: Array.from(stateRaw.inventoryQty || []).map(Number),
        keyItems: Number(stateRaw.keyItems ?? 0),
        playTime: Number(stateRaw.playTime ?? 0),
        startBlock: Number(stateRaw.startBlock ?? 0),
      };

      return state;
    } catch (error) {
      console.error('Failed to get game state for slot:', error);
      return null;
    }
  }, [gameContract, address]);

  // ═══════════════════════════════════════════════════════════════════════
  // MOVEMENT FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Move player in specified direction
   * @param direction Direction enum (0=N, 1=E, 2=S, 3=W)
   */
  const move = useCallback(
    async (direction: Direction): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        gameContract.move!(direction, { gasLimit: GAS_LIMITS.MOVE }),
        'Moved successfully',
        'Failed to move'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Enter a location (town, dungeon, etc.)
   * @param locationId Location ID to enter
   */
  const enterLocation = useCallback(
    async (locationId: number): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        gameContract.enterLocation!(locationId),
        'Entered location',
        'Failed to enter location'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Exit current location and return to world map
   */
  const exitLocation = useCallback(async (): Promise<TransactionResult> => {
    if (!gameContract || !signer) {
      const error = 'Please connect your wallet';
      toast.error(error);
      return { success: false, error };
    }

    return executeTransaction(
      gameContract.exitLocation!(),
      'Exited to world map',
      'Failed to exit location'
    );
  }, [gameContract, signer, executeTransaction, toast]);

  // ═══════════════════════════════════════════════════════════════════════
  // COMBAT FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Get current combat state
   */
  const getCombatState = useCallback(async (): Promise<CombatState | null> => {
    if (!gameContract || !address) return null;
    try {
      const stateRaw = await gameContract.getCombatState!(address);

      // Parse combat state (convert BigInt to number)
      const state: CombatState = {
        active: Boolean(stateRaw.active),
        enemyIds: Array.from(stateRaw.enemyIds || []).map(Number),
        enemyHP: Array.from(stateRaw.enemyHP || []).map(Number),
        turnQueue: Array.from(stateRaw.turnQueue || []).map(Number),
        currentTurn: Number(stateRaw.currentTurn ?? 0),
        canFlee: Boolean(stateRaw.canFlee),
      };

      return state;
    } catch (error) {
      console.error('Failed to get combat state:', error);
      return null;
    }
  }, [gameContract, address]);

  /**
   * Check if player is currently in combat
   */
  const isInCombat = useCallback(async (): Promise<boolean> => {
    if (!gameContract || !address) return false;
    try {
      return await gameContract.isInCombat!(address);
    } catch (error) {
      console.error('Failed to check combat status:', error);
      return false;
    }
  }, [gameContract, address]);

  /**
   * Execute a physical attack in combat
   * @param characterIndex Character performing the attack (0-3)
   * @param targetIndex Enemy target index (0-7)
   */
  const attack = useCallback(
    async (characterIndex: number, targetIndex: number): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      // For the simplified version, we use combatAction from CrystalsGame
      return executeTransaction(
        gameContract.combatAction!(0, characterIndex, targetIndex, 0, { gasLimit: GAS_LIMITS.ATTACK }),
        'Attack executed!',
        'Attack failed'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Cast a spell in combat
   * @param characterIndex Character casting the spell (0-3)
   * @param spellId Spell ID to cast
   * @param targetIndex Target index (enemy 0-7 or ally 0-3)
   */
  const castSpell = useCallback(
    async (characterIndex: number, spellId: number, targetIndex: number): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        gameContract.combatAction!(1, characterIndex, targetIndex, spellId, { gasLimit: GAS_LIMITS.CAST_SPELL }),
        'Spell cast successfully!',
        'Failed to cast spell'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Use an item in combat
   * @param characterIndex Character using the item (0-3)
   * @param itemId Item ID to use
   * @param targetIndex Target character index (0-3)
   */
  const useItem = useCallback(
    async (characterIndex: number, itemId: number, targetIndex: number): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        gameContract.combatAction!(2, characterIndex, targetIndex, itemId, { gasLimit: GAS_LIMITS.USE_ITEM }),
        'Item used!',
        'Failed to use item'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  /**
   * Attempt to flee from combat
   */
  const flee = useCallback(async (): Promise<TransactionResult> => {
    if (!gameContract || !signer) {
      const error = 'Please connect your wallet';
      toast.error(error);
      return { success: false, error };
    }

    return executeTransaction(
      gameContract.combatAction!(4, 0, 0, 0, { gasLimit: GAS_LIMITS.FLEE }),
      'Fled from battle!',
      'Failed to flee'
    );
  }, [gameContract, signer, executeTransaction, toast]);

  /**
   * End combat and collect rewards
   */
  const endCombat = useCallback(async (): Promise<TransactionResult> => {
    if (!gameContract || !signer) {
      const error = 'Please connect your wallet';
      toast.error(error);
      return { success: false, error };
    }

    return executeTransaction(
      gameContract.endCombat!(),
      'Combat ended!',
      'Failed to end combat'
    );
  }, [gameContract, signer, executeTransaction, toast]);

  // ═══════════════════════════════════════════════════════════════════════
  // COMMIT-REVEAL PATTERN (for provable randomness)
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Generate a commitment hash for commit-reveal randomness
   * @param secret Random secret string
   * @param action Action type
   * @returns Commitment hash
   */
  const generateCommitment = useCallback((secret: string, action: number): string => {
    // Convert secret to bytes and create commitment hash
    const secretBytes = toUtf8Bytes(secret);
    const commitment = solidityPackedKeccak256(
      ['bytes32', 'uint8'],
      [keccak256(secretBytes), action]
    );
    return commitment;
  }, []);

  /**
   * Commit to an action requiring randomness
   * @param secret Random secret (keep this safe!)
   * @param actionType Action type identifier
   */
  const commitAction = useCallback(
    async (secret: string, actionType: number): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      const commitment = generateCommitment(secret, actionType);

      return executeTransaction(
        gameContract.commit!(commitment),
        'Commitment submitted',
        'Failed to commit'
      );
    },
    [gameContract, signer, generateCommitment, executeTransaction, toast]
  );

  /**
   * Reveal secret and execute action
   * @param secret The secret from commit phase
   * @param action Action type
   * @param actionData Encoded action parameters
   */
  const revealAndExecute = useCallback(
    async (secret: string, action: number, actionData: string): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      const secretBytes = keccak256(toUtf8Bytes(secret));

      return executeTransaction(
        gameContract.revealAndAct!(secretBytes, action, actionData),
        'Action executed with randomness',
        'Failed to reveal and execute'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // QUERY FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Get player's party members
   */
  const getParty = useCallback(async (): Promise<Character[] | null> => {
    if (!gameContract || !address) return null;
    try {
      const party = await gameContract.getParty!(address);
      return Array.from(party);
    } catch (error) {
      console.error('Failed to get party:', error);
      return null;
    }
  }, [gameContract, address]);

  /**
   * Get player's current position
   */
  const getPosition = useCallback(async (): Promise<{ x: number; y: number; mapId: number } | null> => {
    if (!gameContract || !address) return null;
    try {
      const [x, y, mapId] = await gameContract.getPosition!(address);
      return { x: Number(x), y: Number(y), mapId: Number(mapId) };
    } catch (error) {
      console.error('Failed to get position:', error);
      return null;
    }
  }, [gameContract, address]);

  /**
   * Get player's inventory
   */
  const getInventory = useCallback(async (): Promise<{ itemIds: number[]; quantities: number[] } | null> => {
    if (!gameContract || !address) return null;
    try {
      const state = await gameContract.getGameState!(address);
      return {
        itemIds: Array.from(state.inventory).map(Number),
        quantities: Array.from(state.inventoryQty).map(Number),
      };
    } catch (error) {
      console.error('Failed to get inventory:', error);
      return null;
    }
  }, [gameContract, address]);

  /**
   * Get player's gil (currency)
   */
  const getGil = useCallback(async (): Promise<number> => {
    if (!gameContract || !address) return 0;
    try {
      const state = await gameContract.getGameState!(address);
      return Number(state.gil);
    } catch (error) {
      console.error('Failed to get gil:', error);
      return 0;
    }
  }, [gameContract, address]);

  /**
   * Check if player has a key item
   * @param itemIndex Key item index (0-31)
   */
  const hasKeyItem = useCallback(
    async (itemIndex: number): Promise<boolean> => {
      if (!gameContract || !address) return false;
      try {
        return await gameContract.hasKeyItem!(address, itemIndex);
      } catch (error) {
        console.error('Failed to check key item:', error);
        return false;
      }
    },
    [gameContract, address]
  );

  /**
   * Check if player has a story flag set
   * @param flagIndex Story flag index (0-255)
   */
  const hasStoryFlag = useCallback(
    async (flagIndex: number): Promise<boolean> => {
      if (!gameContract || !address) return false;
      try {
        return await gameContract.hasStoryFlag!(address, flagIndex);
      } catch (error) {
        console.error('Failed to check story flag:', error);
        return false;
      }
    },
    [gameContract, address]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // PARTY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Equip an item to a character
   * @param characterIndex Character index (0-3)
   * @param itemId Item ID to equip
   */
  const equipItem = useCallback(
    async (characterIndex: number, itemId: number): Promise<TransactionResult> => {
      if (!partyContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        partyContract.equipItem!(characterIndex, itemId, { gasLimit: GAS_LIMITS.EQUIP_ITEM }),
        'Item equipped!',
        'Failed to equip item'
      );
    },
    [partyContract, signer, executeTransaction, toast]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // SHOP FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Buy an item from a shop
   * @param shopId Shop identifier
   * @param itemId Item to purchase
   * @param quantity Quantity to buy
   */
  const buyItem = useCallback(
    async (shopId: number, itemId: number, quantity: number): Promise<TransactionResult> => {
      if (!shopContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        shopContract.buyItem!(shopId, itemId, quantity, { gasLimit: GAS_LIMITS.BUY_ITEM }),
        'Item purchased!',
        'Failed to buy item'
      );
    },
    [shopContract, signer, executeTransaction, toast]
  );

  /**
   * Sell an item
   * @param itemId Item to sell
   * @param quantity Quantity to sell
   */
  const sellItem = useCallback(
    async (itemId: number, quantity: number): Promise<TransactionResult> => {
      if (!shopContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        shopContract.sellItem!(itemId, quantity, { gasLimit: GAS_LIMITS.SELL_ITEM }),
        'Item sold!',
        'Failed to sell item'
      );
    },
    [shopContract, signer, executeTransaction, toast]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // INN FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Rest at an inn to restore party
   * @param innId Inn identifier
   */
  const rest = useCallback(
    async (innId: number): Promise<TransactionResult> => {
      if (!innContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      return executeTransaction(
        innContract.rest!(innId, { gasLimit: GAS_LIMITS.REST }),
        'Party rested! HP and MP restored.',
        'Failed to rest'
      );
    },
    [innContract, signer, executeTransaction, toast]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // SAVE GAME TO BLOCKCHAIN
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Save current game state to blockchain
   * @param gameState Complete game state (should be synced with getCurrentGameState())
   * @returns Transaction result
   */
  const saveGameToChain = useCallback(
    async (gameState: GameState): Promise<TransactionResult> => {
      if (!gameContract || !signer) {
        const error = 'Please connect your wallet';
        toast.error(error);
        return { success: false, error };
      }

      // Convert party to CharacterSaveData format for contract
      const partyData = gameState.party.map((char) => ({
        level: char.level,
        experience: char.experience,
        currentHP: char.currentHP,
        maxHP: char.maxHP,
        currentMP: char.currentMP,
        maxMP: char.maxMP,
        strength: char.strength,
        agility: char.agility,
        intelligence: char.intelligence,
        vitality: char.vitality,
        luck: char.luck,
        weapon: char.weapon,
        armor: char.armor,
        helmet: char.helmet,
        gloves: char.gloves,
        accessory: char.accessory,
        status: char.status,
      }));

      // Ensure inventory arrays are the right size (64 items)
      const inventory = [...gameState.inventory];
      const inventoryQty = [...gameState.inventoryQty];
      while (inventory.length < 64) inventory.push(0);
      while (inventoryQty.length < 64) inventoryQty.push(0);

      console.log('[useContract] Saving game to chain:', {
        position: { x: gameState.worldX, y: gameState.worldY },
        map: gameState.currentMap,
        gil: gameState.gil,
        partyLevels: gameState.party.map((c) => c.level),
      });

      return executeTransaction(
        gameContract.saveGame!(
          gameState.worldX,
          gameState.worldY,
          gameState.currentMap,
          gameState.dungeonFloor,
          gameState.gil,
          gameState.storyFlags,
          gameState.keyItems,
          inventory,
          inventoryQty,
          partyData,
          { gasLimit: 500000 } // Higher gas limit for full state write
        ),
        'Game saved to blockchain!',
        'Failed to save game to chain'
      );
    },
    [gameContract, signer, executeTransaction, toast]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // EVENT LISTENERS
  // ═══════════════════════════════════════════════════════════════════════

  /**
   * Subscribe to combat started events
   * @param callback Function to call when combat starts
   * @returns Cleanup function
   */
  const onCombatStarted = useCallback(
    (callback: (enemyIds: number[]) => void) => {
      if (!gameContract) return () => {};

      const filter = gameContract.filters.CombatStarted!(address);
      const handler = (...args: unknown[]) => {
        const event = args[args.length - 1] as { args: { enemyIds: number[] } };
        callback(Array.from(event.args.enemyIds).map(Number));
      };

      gameContract.on(filter, handler);

      return () => {
        gameContract.off(filter, handler);
      };
    },
    [gameContract, address]
  );

  /**
   * Subscribe to combat ended events
   * @param callback Function to call when combat ends
   * @returns Cleanup function
   */
  const onCombatEnded = useCallback(
    (callback: (victory: boolean, exp: number, gil: number) => void) => {
      if (!gameContract) return () => {};

      const victoryHandler = (...args: unknown[]) => {
        const event = args[args.length - 1] as { args: { expGained: bigint; gilGained: bigint } };
        callback(true, Number(event.args.expGained), Number(event.args.gilGained));
      };

      const defeatHandler = () => {
        callback(false, 0, 0);
      };

      gameContract.on(gameContract.filters.CombatVictory!(address), victoryHandler);
      gameContract.on(gameContract.filters.CombatDefeat!(address), defeatHandler);

      return () => {
        gameContract.off(gameContract.filters.CombatVictory!(address), victoryHandler);
        gameContract.off(gameContract.filters.CombatDefeat!(address), defeatHandler);
      };
    },
    [gameContract, address]
  );

  /**
   * Subscribe to player moved events
   * @param callback Function to call when player moves
   * @returns Cleanup function
   */
  const onPlayerMoved = useCallback(
    (callback: (x: number, y: number, mapId: number) => void) => {
      if (!gameContract) return () => {};

      const filter = gameContract.filters.PlayerMoved!(address);
      const handler = (...args: unknown[]) => {
        const event = args[args.length - 1] as { args: { x: bigint; y: bigint; mapId: bigint } };
        callback(Number(event.args.x), Number(event.args.y), Number(event.args.mapId));
      };

      gameContract.on(filter, handler);

      return () => {
        gameContract.off(filter, handler);
      };
    },
    [gameContract, address]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // RETURN OBJECT
  // ═══════════════════════════════════════════════════════════════════════

  return {
    // Contract state
    isLoading,
    error,
    isReady: !!gameContract && isConnected,
    isConnected,

    // Contract instances (for advanced usage)
    gameContract,
    combatContract,
    partyContract,
    magicContract,
    worldContract,
    shopContract,
    innContract,

    // Game management
    hasGame,
    hasActiveGame: hasGame, // Alias for compatibility
    getGameState,
    getGameStateForSlot,
    startNewGame,
    createGame: startNewGame, // Alias for compatibility

    // Slot management
    getSlotInfo,
    selectSlot,
    deleteSlot,

    // Movement
    move,
    enterLocation,
    exitLocation,

    // Combat
    getCombatState,
    isInCombat,
    attack,
    castSpell,
    useItem,
    flee,
    endCombat,

    // Commit-reveal randomness
    generateCommitment,
    commitAction,
    revealAndExecute,

    // Query functions
    getParty,
    getPosition,
    getInventory,
    getGil,
    hasKeyItem,
    hasStoryFlag,

    // Party management
    equipItem,

    // Shop
    buyItem,
    sellItem,

    // Inn
    rest,

    // Save to blockchain
    saveGameToChain,

    // Event listeners
    onCombatStarted,
    onCombatEnded,
    onPlayerMoved,
  };
};
